import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const cls = {
    clean: "bg-emerald-100 text-emerald-800",
    approved: "bg-emerald-100 text-emerald-800",
    pending_review: "bg-amber-100 text-amber-800",
    rejected: "bg-red-100 text-red-800",
  }[status] || "bg-gray-100 text-gray-700";
  return <Badge className={`${cls} text-xs`}>{status.replace("_", " ")}</Badge>;
};

export default function PartnerCourses() {
  const loc = useLocation();
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(loc.pathname.endsWith("/new"));
  const [form, setForm] = useState({
    title: "", slug: "", short_description: "", description: "",
    price_usd: 49, level: "Beginner", language: "English",
  });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/api/partner/courses").then(({ data }) => setCourses(data));
  useEffect(() => { load(); }, []);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const create = async () => {
    if (!form.title || !form.slug) return toast.error("Title and slug required");
    setBusy(true);
    try {
      const payload = { ...form, price_usd: Number(form.price_usd) };
      await api.post("/api/partner/courses", payload);
      setOpen(false);
      setForm({ title: "", slug: "", short_description: "", description: "",
        price_usd: 49, level: "Beginner", language: "English" });
      await load();
      toast.success("Course created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (c) => {
    try {
      if (c.is_published) {
        await api.post(`/api/partner/courses/${c.id}/unpublish`);
      } else {
        await api.post(`/api/partner/courses/${c.id}/publish`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/partner/courses/${c.id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="max-w-6xl space-y-6" data-testid="partner-courses-root">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Courses</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#DC2626] hover:bg-[#B91C1C]"
              data-testid="partner-create-course-btn">
              + New course
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="partner-create-course-dialog">
            <DialogHeader>
              <DialogTitle>New course</DialogTitle>
              <DialogDescription>
                Create a new course under your partner account. You can add modules and publish it after.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Title *</Label>
                <Input data-testid="partner-course-title-input"
                  value={form.title} onChange={update("title")} />
              </div>
              <div>
                <Label>URL slug *</Label>
                <Input data-testid="partner-course-slug-input"
                  value={form.slug} onChange={update("slug")}
                  placeholder="e.g. patient-mobility-101" />
              </div>
              <div>
                <Label>Short description</Label>
                <Input data-testid="partner-course-short-input"
                  value={form.short_description} onChange={update("short_description")} />
              </div>
              <div>
                <Label>Full description</Label>
                <Textarea rows={4} data-testid="partner-course-desc-input"
                  value={form.description} onChange={update("description")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Price (USD)</Label>
                  <Input type="number" data-testid="partner-course-price-input"
                    value={form.price_usd} onChange={update("price_usd")} />
                </div>
                <div>
                  <Label>Level</Label>
                  <select className="w-full h-10 px-3 rounded-md border"
                    data-testid="partner-course-level-input"
                    value={form.level} onChange={update("level")}>
                    <option>Beginner</option>
                    <option>Intermediate</option>
                    <option>Advanced</option>
                  </select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}
                data-testid="partner-create-cancel-btn">Cancel</Button>
              <Button onClick={create} disabled={busy}
                data-testid="partner-create-submit-btn"
                className="bg-[#DC2626] hover:bg-[#B91C1C]">
                {busy ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {courses.length === 0 ? (
        <Card data-testid="partner-courses-empty">
          <CardContent className="p-10 text-center text-gray-500">
            No courses yet. Click "New course" to get started.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {courses.map((c) => (
            <Card key={c.id} data-testid="partner-course-row">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-semibold truncate">{c.title}</div>
                    {c.is_published ? (
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">Published</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">Draft</Badge>
                    )}
                    <StatusBadge status={c.compliance_status || "clean"} />
                  </div>
                  <div className="text-sm text-gray-500 truncate">/{c.slug} · ${c.price_usd}</div>
                  {c.compliance_flags?.length > 0 && (
                    <div className="mt-1 text-xs text-amber-800">
                      Flags: {c.compliance_flags.join(", ")}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/partner/courses/${c.id}/edit`}>
                    <Button variant="outline" size="sm" data-testid="partner-course-edit-btn">
                      Edit
                    </Button>
                  </Link>
                  <Button variant="outline" size="sm" onClick={() => togglePublish(c)}
                    data-testid="partner-course-toggle-publish-btn">
                    {c.is_published ? "Unpublish" : "Publish"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => del(c)}
                    data-testid="partner-course-delete-btn"
                    className="text-red-600">
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
